/**
 * Labstep
 *
 * @module components/ExperimentWorkflow/Action/CreateWithFolder
 * @desc Create an experiment workflow by selecting a folder (and optional template)
 */

import { FieldType } from 'labstep-web/constants/form';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { EntityCanCreateContainer } from 'labstep-web/containers/Entity/Create/Can';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { FormReusable } from 'labstep-web/core/Form/Reusable';
import type { IFieldProps } from 'labstep-web/core/Form/Reusable/types';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Folder } from 'labstep-web/models/folder.model';
import { navigation } from 'labstep-web/services/navigation';
import rules from 'labstep-web/services/validation/rules';
import type React from 'react';
import { withRouter } from 'react-router';
import { object } from 'yup';
import type { ExperimentWorkflowActionCreateWithFolderProps } from './types';

export const ExperimentWorkflowActionCreateWithFolder: React.FC<
  ExperimentWorkflowActionCreateWithFolderProps
> = ({ actionComponentProps, activeGroup, history }) => (
  <Modal
    header="New Experiment"
    viewComponent={
      <ActionComponent
        type="button"
        text="New Experiment"
        {...actionComponentProps}
      />
    }
    usePortal
    content={({ toggleModal }) => (
      <EntityCanCreateContainer
        entityName={ExperimentWorkflow.entityName}
        showLoaderWhileCreating
      >
        {({ status, create }) => {
          // Folder field (required)
          const fieldFolder: IFieldProps = {
            fieldType: FieldType.SearchSelect,
            name: 'folder',
            fieldLabel: Folder.getHumanReadableEntityName(
              false,
              true,
            ),
            validation: object().required(),
            elementProps: {
              entityName: Folder.entityName,
              params: {
                group_id: activeGroup?.id,
                type: ExperimentWorkflow.entityName,
              },
              // ensure we submit GUID (previous implementation used selectedOption.guid)
              valueKey: 'guid',
              searchKey: 'search_query',
              creatable: true,
              createBody: {
                group_id: activeGroup?.id,
                type: ExperimentWorkflow.entityName,
              },
            },
          };

          // Optional template field
          const fieldTemplate: IFieldProps = {
            fieldType: FieldType.SearchSelect,
            name: 'source',
            fieldLabel: 'Template (optional)',
            validation: rules.experiment_workflow.template,
            elementProps: {
              entityName: ExperimentWorkflow.entityName,
              placeholder: 'Search templates...',
              params: {
                is_template: true,
                group_id: activeGroup?.id,
              },
              searchKey: 'search_query',
              isClearable: true,
            },
          };

          return (
            <FormReusable
              autoFocus
              status={status}
              submitButtonText="Create"
              onCancel={toggleModal}
              onSubmit={(values) => {
                // preSubmit transforms search selects to *_id (folder_id uses guid due to valueKey)
                const { folder_id, source_id } = values as Record<
                  string,
                  any
                >;
                create(
                  {
                    folder_id, // GUID
                    ...(source_id ? { source_id } : {}),
                  },
                  {
                    onSuccess: ({ response }) => {
                      if (!response) {
                        return;
                      }
                      history.push(
                        navigation.get('experiment_workflow_show', {
                          id: response.result,
                        }),
                      );
                      toggleModal();
                    },
                  },
                );
              }}
              fields={[fieldFolder, fieldTemplate]}
            />
          );
        }}
      </EntityCanCreateContainer>
    )}
  />
);

export default withRouter(
  withActiveGroup(ExperimentWorkflowActionCreateWithFolder),
);
